import React, {
  Fragment,
  ReactNode,
  ReactElement,
  isValidElement,
} from "react";
import { v4 as uuid } from "uuid";
import { ICON_MAP } from "@app/constants/icons";

const isElementWithChildren = (
  node: ReactNode,
): node is ReactElement<{ children?: ReactNode }> => {
  return (
    isValidElement(node) &&
    typeof node.props === "object" &&
    "children" in node.props
  );
};

export const replaceTagsWithIcons = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\{\{.*?\}\})/g);

  return parts.map((part) => {
    const key = uuid();
    const match = part.match(/^\{\{(.*?)\}\}$/);

    if (match) {
      const iconName = match[1].trim();
      const IconComponent = ICON_MAP[iconName];

      if (IconComponent) {
        return (
          <span key={`icon-${key}`} className="inline-flex align-middle">
            <IconComponent className="h-[1em]" />
          </span>
        );
      }

      console.warn(`[replaceTagsWithIcons] Unknown icon tag: {{${iconName}}}`);

      return <Fragment key={`unknown-${key}`}>{`{{${iconName}}}`}</Fragment>;
    }

    return <Fragment key={`text-${key}`}>{part}</Fragment>;
  });
};

export const transformRichTextChildren = (children: ReactNode): ReactNode => {
  if (typeof children === "string") {
    return replaceTagsWithIcons(children);
  }

  if (Array.isArray(children)) {
    return children.flatMap((child) => transformRichTextChildren(child));
  }

  if (isElementWithChildren(children)) {
    const transformedChildren = transformRichTextChildren(
      children.props.children,
    );

    return React.cloneElement(children, {
      ...children.props,
      children: transformedChildren,
    });
  }

  return children;
};
